import { useEffect, useState } from "react";
import axios from "axios";
import { Trash2, Upload } from "lucide-react";
import { SERVICES } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PhotoManager({ onUnauth }) {
  const [photos, setPhotos] = useState({});

  const load = () => {
    axios
      .get(`${API}/gallery`)
      .then((r) => setPhotos(groupByService(r.data)))
      .catch((e) => e?.response?.status === 401 && onUnauth());
  };

  const groupByService = (list) => {
    const map = {};
    list.forEach((p) => (map[p.service] = [...(map[p.service] || []), p]));
    return map;
  };

  useEffect(load, []);

  const upload = async (service, input) => {
    const file = input.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("service", service);
    fd.append("file", file);
    try {
      await axios.post(`${API}/admin/photos`, fd, { withCredentials: true });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Upload failed — use a JPG/PNG/WebP under 5 MB.");
    }
  };

  const removePhoto = async (id) => {
    await axios.delete(`${API}/admin/photos/${id}`, { withCredentials: true });
    load();
  };

  return (
    <div>
      <p className="mb-5 text-sm text-clay">
        Upload real event photos for each service — they instantly replace the placeholder photo on
        the website. Latest upload wins.
      </p>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {SERVICES.map((s, i) => (
          <div key={s.title} data-testid={`photo-manager-${i + 1}`} className="rounded-xl border border-gold-faint bg-ivory p-4">
            <h3 className="font-serif text-lg font-semibold">{s.title}</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {s.title && (photos[s.title] || []).length === 0 && (
                <div className="flex h-20 w-full items-center justify-center rounded-md border border-dashed border-gold-faint text-[11px] uppercase tracking-[0.2em] text-clay">
                  Placeholder live
                </div>
              )}
              {(photos[s.title] || []).map((p) => (
                <div key={p.id} className="group relative h-20 w-24 overflow-hidden rounded-md border border-gold-faint">
                  <img src={p.url} alt={s.title} className="h-full w-full object-cover" />
                  <button
                    data-testid={`photo-delete-button-${p.id}`}
                    onClick={() => removePhoto(p.id)}
                    title="Remove photo"
                    className="absolute inset-0 flex items-center justify-center bg-night/70 text-cream opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <label
              data-testid={`photo-upload-label-${i + 1}`}
              className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-full border border-gold-faint py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-dark transition-colors hover:bg-gold hover:text-night"
            >
              <Upload className="h-3.5 w-3.5" /> Upload photo
              <input
                data-testid={`photo-upload-input-${i + 1}`}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => upload(s.title, e.target)}
              />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}
